import { Button, Modal, Label, TextInput } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { LocationAlias } from "@/sharedTypes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useGetCitiesQuery } from "@/api/locationApi/city";
import { useGetDistrictsQuery } from "@/api/locationApi/district";

interface LocationAliasFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LocationAlias) => void;
  defaultValues?: LocationAlias;
  mode?: "create" | "edit";
  errorMessage?: string;
}

// A single alias resolves to exactly one city OR one district. Rather than a
// toggle + two conditional selects, this combines both into one searchable
// list (value encoded as "city:<id>" / "district:<id>") — simpler for the
// admin than picking a type first.
type Resolved = { kind: "city" | "district"; id: string } | null;

export default function LocationAliasFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = "create",
  errorMessage,
}: LocationAliasFormDialogProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LocationAlias & { resolved?: Resolved }>();

  const { data: citiesData } = useGetCitiesQuery({ pagination: { page: 1, pageSize: 100 } });
  const { data: districtsData } = useGetDistrictsQuery({ pagination: { page: 1, pageSize: 100 } });

  const [search, setSearch] = useState("");

  const allOptions = useMemo(() => {
    const cityOpts = (citiesData?.items || []).map((c: any) => ({
      label: `${t("temporalActivity:locationAlias.cityPrefix")}: ${c.city_name}`,
      value: `city:${c.id}`,
    }));
    const districtOpts = (districtsData?.items || []).map((d: any) => ({
      label: `${t("temporalActivity:locationAlias.districtPrefix")}: ${d.district_name}${d.city?.city_name ? ` (${d.city.city_name})` : ""}`,
      value: `district:${d.id}`,
    }));
    return [...cityOpts, ...districtOpts];
  }, [citiesData, districtsData, t]);

  const filteredOptions = useMemo(() => {
    if (!search) return allOptions;
    const normalized = search.toLowerCase();
    return allOptions.filter((o) => o.label.toLowerCase().includes(normalized));
  }, [allOptions, search]);

  useEffect(() => {
    if (open) {
      let resolved: Resolved = null;
      if (defaultValues?.canonical_city_id) {
        resolved = { kind: "city", id: String(defaultValues.canonical_city_id) };
      } else if (defaultValues?.canonical_district_id) {
        resolved = { kind: "district", id: String(defaultValues.canonical_district_id) };
      }
      reset({
        alias_text: defaultValues?.alias_text || "",
        resolved,
      } as any);
      setSearch("");
    }
  }, [open, defaultValues, reset]);

  const handleFormSubmit = (data: any) => {
    const resolved: Resolved = data.resolved;
    onSubmit({
      alias_text: data.alias_text,
      canonical_city_id: resolved?.kind === "city" ? Number(resolved.id) : null,
      canonical_district_id: resolved?.kind === "district" ? Number(resolved.id) : null,
    } as LocationAlias);
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>
        {mode === "edit" ? t("temporalActivity:locationAlias.edit") : t("temporalActivity:locationAlias.create")}
      </Modal.Header>
      <Modal.Body className="overflow-visible">
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="alias_text" value={t("temporalActivity:locationAlias.aliasText")} />
            </div>
            <TextInput
              id="alias_text"
              placeholder={t("temporalActivity:locationAlias.aliasTextPlaceholder")}
              {...register("alias_text", {
                required: t("temporalActivity:locationAlias.aliasTextRequired") as string,
              })}
              color={errors.alias_text ? "failure" : "gray"}
              helperText={errors.alias_text?.message}
            />
          </div>

          <Controller
            name={"resolved" as any}
            control={control}
            rules={{ required: t("temporalActivity:locationAlias.resolvedRequired") as string }}
            render={({ field }) => {
              const value: Resolved = field.value;
              const selected = value
                ? allOptions.find((o) => o.value === `${value.kind}:${value.id}`) ?? null
                : null;
              return (
                <PaginatedSelect
                  id="resolved"
                  label={t("temporalActivity:locationAlias.resolvesTo")}
                  value={selected}
                  onChange={(val: any) => {
                    if (!val) {
                      field.onChange(null);
                      return;
                    }
                    const [kind, id] = String(val.value).split(":");
                    field.onChange({ kind, id });
                  }}
                  options={filteredOptions}
                  onInputChange={setSearch}
                  placeholder={t("temporalActivity:locationAlias.selectResolvesTo")}
                  page={1}
                  totalPages={1}
                  onPageChange={() => {}}
                />
              );
            }}
          />
          {(errors as any).resolved && (
            <p className="text-sm text-red-500">{(errors as any).resolved.message}</p>
          )}

          {errorMessage && <div className="text-sm text-red-500">{errorMessage}</div>}

          <div className="flex justify-end gap-x-2">
            <Button color="gray" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {mode === "edit" ? t("common:save") : t("common:create")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
